import type { Exchange, ExchangeItem } from "./exchange.js";

export const IMPORT_BYTES = 8 * 1024 * 1024;
export const IMPORT_ITEMS = 999;

export class ImportError extends Error {
  constructor(readonly line: number, message: string) { super(`Line ${line}: ${message}`); }
}

/** The format versions import reads. Version 2 (g79) adds a category line after the quantity. */
export type ExchangeVersion = 1 | 2;
type Located = { value: Exchange; lines: number[]; nameLine: number; version: ExchangeVersion };
const fail = (line: number, message: string): never => { throw new ImportError(line, message); };

function decode(source: string): Located {
  const oversized = () => fail(1, "This document exceeds the 8 MiB import limit.");
  if (source.length > IMPORT_BYTES) oversized();
  // Count without allocating a complete UTF-8 copy. Unpaired surrogates cost
  // three replacement bytes, matching TextEncoder; content validation rejects them.
  let bytes = 0;
  for (let index = 0; index < source.length; index++) {
    const point = source.codePointAt(index)!;
    bytes += point <= 0x7f ? 1 : point <= 0x7ff ? 2 : point <= 0xffff ? 3 : 4;
    if (point > 0xffff) index++;
    if (bytes > IMPORT_BYTES) oversized();
  }
  // Read one physical line at a time: 8 MiB of newlines must not allocate
  // millions of array entries before the first grammar error is reported.
  let offset = 0;
  const read = (): string | undefined => {
    if (offset >= source.length) return undefined;
    const end = source.indexOf("\n", offset);
    let line = source.slice(offset, end === -1 ? source.length : end);
    offset = end === -1 ? source.length : end + 1;
    if (end !== -1 && line.endsWith("\r")) line = line.slice(0, -1);
    return line;
  };
  let next = read();
  let at = 0;
  const take = () => { const line = next; next = read(); at++; return line; };
  const literal = (expected: string) => {
    if (take() !== expected) fail(at, `Expected ${expected.slice(0, 60)}.`);
  };
  const scalar = (prefix: string, type: "string" | "nullable" | "boolean"): unknown => {
    const line = take();
    if (!line?.startsWith(prefix)) fail(at, `Expected ${prefix.trim()}.`);
    const raw = line!.slice(prefix.length);
    if (type === "boolean") {
      if (raw === "true") return true;
      if (raw === "false") return false;
      return fail(at, "Use true or false for done.");
    }
    if (type === "nullable" && raw === "null") return null;
    // JSON.parse accepts surrounding whitespace; the exchange grammar does not.
    if (!raw.startsWith('"') || !raw.endsWith('"') || /[\u0000-\u001f\u007f-\u009f\u2028\u2029\ufffe\uffff]/u.test(raw)) {
      fail(at, "Use a double-quoted string with escaped control characters.");
    }
    // Literal isolated surrogates are not emitted; paired astral characters are valid.
    if (/[\ud800-\udfff]/u.test(raw)) fail(at, "Escape isolated surrogate characters with \\uXXXX.");
    try {
      const value: unknown = JSON.parse(raw);
      if (typeof value === "string") return value;
    } catch { /* Report the bounded grammar error below. */ }
    return fail(at, "Use a valid double-quoted string with JSON escapes.");
  };
  const header = take();
  const version: ExchangeVersion = header === "version: 2" ? 2 : header === "version: 1" ? 1
    : fail(at, "Expected version: 2 or version: 1.");
  const name = scalar("name: ", "string") as string;
  const kind = scalar("kind: ", "string") as Exchange["kind"];
  const items: ExchangeItem[] = [];
  const positions: number[] = [];
  if (next === "items: []") take();
  else {
    literal("items:");
    while (next !== undefined) {
      if (items.length === IMPORT_ITEMS) fail(at + 1, "Import accepts at most 999 items.");
      positions.push(at + 1);
      const text = scalar("  - text: ", "string") as string;
      const quantity = scalar("    quantity: ", "nullable") as string | null;
      const category = version === 2 ? scalar("    category: ", "nullable") as string | null : null;
      const note = scalar("    note: ", "nullable") as string | null;
      let due: ExchangeItem["due"] = null;
      if (next === "    due: null") take();
      else {
        literal("    due:");
        due = { date: scalar("      date: ", "string") as string,
          time: scalar("      time: ", "nullable") as string | null,
          tz: scalar("      tz: ", "nullable") as string | null };
      }
      const done = scalar("    done: ", "boolean") as boolean;
      const repeat = scalar("    repeat: ", "nullable") as ExchangeItem["repeat"];
      const repeatAnchor = scalar("    repeatAnchor: ", "nullable") as string | null;
      items.push({ text, quantity, category, note, due, done, repeat, repeatAnchor });
    }
    if (!items.length) fail(at + 1, "Write items: [] for an empty list.");
  }
  if (next !== undefined) fail(at + 1, "Remove the extra content after the list.");
  return { value: { name, kind, items }, lines: positions, nameLine: 2, version };
}

/** Syntax round trips defensive emitter fixtures; importing also requires validation. */
export function decodeExchange(source: string): Exchange { return decode(source).value; }

function date(value: string): boolean {
  if (!/^(19\d\d|2\d{3})-\d\d-\d\d$/.test(value)) return false;
  const parsed = new Date(value + "T00:00:00Z");
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

/**
 * Validate again at the store boundary, even for callers that did not use the form.
 * Line numbers follow the document's version: version 2 has one more line per item.
 */
export function validateExchange(value: Exchange, positions?: number[], version: ExchangeVersion = 2): void {
  // Lines after the quantity sit one further down in version 2.
  const shift = version === 2 ? 1 : 0;
  const string = (v: string, max: number, line: number, label: string, multiline = false) => {
    if (typeof v !== "string" || !v.length || v.length > max) fail(line, `${label} must contain 1 to ${max} characters.`);
    if (v !== v.trim()) fail(line, `Remove surrounding whitespace from ${label}.`);
    if ((multiline ? /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/ : /[\u0000-\u001f\u007f]/).test(v)) fail(line, `${label} contains a forbidden control character.`);
    if (/[\ud800-\udfff]/u.test(v)) fail(line, `${label} contains an unpaired Unicode surrogate. Replace that character.`);
  };
  string(value.name, 200, 2, "name");
  if (!["todo", "shopping", "other"].includes(value.kind)) fail(3, "Use todo, shopping or other for kind.");
  if (value.items.length > IMPORT_ITEMS) fail(4, "Import accepts at most 999 items.");
  const zones = new Set<string>();
  value.items.forEach((item, index) => {
    const line = positions?.[index] ?? 5 + index * (7 + shift);
    string(item.text, 1000, line, "text");
    if (item.quantity !== null) string(item.quantity, 50, line + 1, "quantity");
    if (item.category !== null) string(item.category, 64, line + 2, "category");
    const after = line + shift;
    if (item.note !== null) string(item.note, 10_000, after + 2, "note", true);
    if (item.due) {
      if (!date(item.due.date)) fail(after + 4, "Use a real date between 1900-01-01 and 2999-12-31.");
      if ((item.due.time === null) !== (item.due.tz === null)) fail(after + 5, "Set time and timezone together, or make both null.");
      if (item.due.time !== null && !/^([01]\d|2[0-3]):[0-5]\d$/.test(item.due.time)) fail(after + 5, "Use a real time in HH:mm format.");
      if (item.due.tz !== null && !zones.has(item.due.tz)) {
        try { new Intl.DateTimeFormat("en", { timeZone: item.due.tz }); zones.add(item.due.tz); }
        catch { fail(after + 6, "Use a recognized timezone."); }
      }
    }
    const doneLine = after + (item.due ? 7 : 4);
    if (typeof item.done !== "boolean") fail(doneLine, "Use true or false for done.");
    if (item.repeat !== null && !["daily", "weekly", "monthly", "yearly"].includes(item.repeat)) fail(doneLine + 1, "Use daily, weekly, monthly, yearly or null for repeat.");
    if (item.repeat) {
      if (item.done) fail(doneLine, "A repeating item must have done: false.");
      if (!item.due) fail(after + 3, "A repeating item needs a due date.");
      if (!item.repeatAnchor || !date(item.repeatAnchor) || item.repeatAnchor > item.due!.date) fail(doneLine + 2, "Use a valid repeat anchor no later than the due date.");
    } else if (item.repeatAnchor !== null) fail(doneLine + 2, "A non-repeating item needs repeatAnchor: null.");
  });
}

export function parseExchange(source: string): Exchange {
  const parsed = decode(source);
  validateExchange(parsed.value, parsed.lines, parsed.version);
  return parsed.value;
}
